import { cn } from "@/lib/utils";

/*
 * A flat-top tower crane lowering a load onto a building under construction,
 * drawn in the style of De Ceuster's technical icons: thin, even line work
 * without fill (the strokes keep their width at any size). Coordinates are in
 * viewBox units (520 × 640); the lattices are generated, so they stay regular.
 */

const VIEW_BOX = "0 0 520 640";

/** Rounds to one decimal, to keep the path data short. */
function n(value: number): string {
  return String(Math.round(value * 10) / 10);
}

/** The mast: two legs, a rung at every panel and a zigzag brace through them. */
function mastPath(left: number, right: number, top: number, bottom: number, panels: number) {
  const step = (bottom - top) / panels;
  const parts = [`M${left} ${top}V${bottom}`, `M${right} ${top}V${bottom}`];
  for (let i = 0; i <= panels; i++) {
    parts.push(`M${left} ${n(top + i * step)}H${right}`);
  }
  for (let i = 0; i < panels; i++) {
    const [from, to] = i % 2 === 0 ? [left, right] : [right, left];
    parts.push(`M${from} ${n(top + i * step)}L${to} ${n(top + (i + 1) * step)}`);
  }
  return parts.join("");
}

/**
 * A jib: a straight bottom chord, a top chord that tapers towards the far end,
 * both ends closed, and a Warren zigzag between the chords.
 */
function trussPath(
  root: number,
  end: number,
  rootTop: number,
  endTop: number,
  bottom: number,
  panels: number,
) {
  const topAt = (x: number) => rootTop + ((endTop - rootTop) * (x - root)) / (end - root);
  const step = (end - root) / panels;
  const zigzag = Array.from({ length: panels + 1 }, (_, i) => {
    const x = root + i * step;
    return `${n(x)} ${n(i % 2 === 0 ? bottom : topAt(x))}`;
  });
  return [
    `M${root} ${rootTop}L${end} ${endTop}`,
    `M${root} ${bottom}H${end}`,
    `M${root} ${rootTop}V${bottom}`,
    `M${end} ${endTop}V${bottom}`,
    `M${zigzag.join("L")}`,
  ].join("");
}

const MAST = mastPath(330, 362, 132, 598, 14);
const JIB = trussPath(322, 36, 96, 106, 118, 14);
const COUNTER_JIB = trussPath(370, 490, 96, 102, 118, 4);

interface CraneIllustrationProps {
  /** Size and position; the drawing keeps its proportions and sits on its base line. */
  className?: string;
}

/**
 * De Ceuster's tower crane as a decorative line drawing, in `currentColor`
 * (white on the brand panel). Hidden from assistive technology.
 */
export function CraneIllustration({ className }: CraneIllustrationProps) {
  return (
    <svg
      viewBox={VIEW_BOX}
      preserveAspectRatio="xMidYMax meet"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.25}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={cn("[&_*]:[vector-effect:non-scaling-stroke]", className)}
    >
      {/* Ground and foundation */}
      <path d="M16 610H504" />
      <path d="M304 598H388V610H304Z" />
      {/* Mast, slewing unit and cab */}
      <path d={MAST} />
      <rect x={322} y={118} width={48} height={14} />
      <rect x={370} y={118} width={30} height={32} />
      <rect x={376} y={124} width={18} height={12} />
      {/* Jib and counter-jib with its winch and counterweights */}
      <path d={JIB} />
      <path d={COUNTER_JIB} />
      <rect x={402} y={85} width={26} height={12} />
      <rect x={446} y={118} width={38} height={30} />
      <path d="M446 128H484M446 138H484" />
      {/* Trolley, hoist ropes, hook block and hook */}
      <rect x={136} y={118} width={28} height={8} />
      <path d="M146 126V358M154 126V358" />
      <rect x={138} y={358} width={24} height={22} />
      <circle cx={150} cy={369} r={6} />
      <path d="M150 380V394A8 8 0 0 1 134 394V389" />
      {/* Slings and the load: three precast blocks */}
      <path d="M142 402L104 440M142 402L196 440" />
      <rect x={96} y={440} width={108} height={22} />
      <path d="M132 440V462M168 440V462" />
      {/* The building going up under the jib */}
      <path d="M52 500H248M52 552H248" />
      <path d="M60 488V610M120 488V610M180 488V610M240 488V610" />
    </svg>
  );
}
